import React, { useContext, useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Divider from '@mui/material/Divider'
import Collapse from '@mui/material/Collapse'
import Checkbox from '@mui/material/Checkbox'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import SortIcon from '@mui/icons-material/Sort'
import {
  ReportFieldsFragment,
  ReportListItem,
  ReportTagging,
  ReportTagsFieldsFragment,
  SimpleReportFieldsFragment,
} from 'gqlTypes'
import { CurrentTenantContext } from '../contexts/CurrentUserContext'
import isEmpty from 'lodash/fp/isEmpty'
import sum from 'lodash/fp/sum'
import CommonSidebar from './CommonSidebar'

/*
 * The `filterReducer` and `initFilters` functions should be imported by the
 * parent component and passed to `useReducer`. The resulting filter state and
 * dispatch method can then be passed to the ReportFilters component.
 *
 * The `filterReports` function should also be imported and used by the parent
 * to generate a filtered list of reports. The filtered list can be passed to
 * the ReportFilters component to display updated counts of tagged reports.
 *
 * The result of `filterReports` should be memoized by the parent component.
 */

type TagCategory = keyof Omit<ReportTagsFieldsFragment, '__typename'>

type TagCounts = Record<TagCategory, Record<string, number>>

type FilterState = Record<TagCategory, string[]>

type FilterAction =
  | { type: 'clearFilters' }
  | { type: 'addFilter'; category: TagCategory; tag: string }
  | { type: 'removeFilter'; category: TagCategory; tag: string }

export const CATEGORIES: TagCategory[] = [
  'surveyType',
  'reportTypeTags',
  'yearTags',
  'levelTags',
  'schoolTags',
  'districtTags',
  'commentsTags',
]

export function initFilters(): FilterState {
  return {
    surveyType: [],
    reportTypeTags: [],
    yearTags: [],
    levelTags: [],
    schoolTags: [],
    districtTags: [],
    commentsTags: [],
  }
}

export function filterReducer(
  state: FilterState,
  action: FilterAction
): FilterState {
  switch (action.type) {
    case 'clearFilters':
      return initFilters()
    case 'addFilter': {
      const tags = state[action.category]
      return { ...state, [action.category]: tags.concat(action.tag) }
    }
    case 'removeFilter': {
      const tags = state[action.category]
      return {
        ...state,
        [action.category]: tags.filter((t) => t !== action.tag),
      }
    }
  }
}

export function filterReports(
  reports: ReportListItem[],
  filters: FilterState,
  reportTaggings: ReportTagging[]
) {
  const noFilterSelected = Object.values(filters).every((f) => !f.length)
  if (noFilterSelected) {
    return reports
  }
  return reports.filter(({ id }) => {
    return CATEGORIES.every((category) => {
      if (!filters[category].length) {
        return true
      }
      const taggings = reportTaggings.find((d) => d.reportId === id)?.tags
      const tags = taggings ? taggings[category] || [] : []
      return tags.some((tag) => {
        return filters[category].includes(tag)
      })
    })
  })
}

function countTags(reportTaggings: ReportTagging[]): TagCounts {
  let counts: TagCounts = {
    surveyType: {},
    reportTypeTags: {},
    yearTags: {},
    levelTags: {},
    schoolTags: {},
    districtTags: {},
    commentsTags: {},
  }
  reportTaggings.forEach(({ tags }) => {
    CATEGORIES.forEach((category) => {
      tags[category].forEach((tag) => {
        counts[category][tag] ||= 0
        counts[category][tag] += 1
      })
    })
  })
  return counts
}

interface ReportFiltersProps {
  reports: ReportListItem[]
  filters: FilterState
  reportTaggings: ReportTagging[]
  dispatch: React.Dispatch<FilterAction>
}

export const ReportFilters: React.FC<ReportFiltersProps> = ({
  reports,
  filters,
  reportTaggings,
  dispatch,
}) => {
  const tenant = useContext(CurrentTenantContext)

  const [filterListOpen, setFilterListOpen] = useState(
    CATEGORIES.reduce(
      (result, category) => ({ ...result, [category]: false }),
      {} as { [key: string]: boolean }
    )
  )
  const tags = useMemo(
    () => countTags(reportTaggings),
    [reports, reportTaggings]
  )
  const filterCount = sum(Object.values(filters).map((tags) => tags.length))
  const categories: [TagCategory, string][] = [
    ['surveyType', 'Survey Type'],
    ['reportTypeTags', 'Report Type'],
    // ['yearTags', 'Year'],
    ['levelTags', 'Level'],
    ['schoolTags', 'School'],
    ['districtTags', 'District'],
    ['commentsTags', 'Comments'],
  ]
  const tenantCategories: [TagCategory, string][] = categories.filter((cat) => {
    if (tenant && tenant.title === 'CEP') {
      return ![
        'levelTags',
        'schoolTags',
        'districtTags',
        'commentsTags',
      ].includes(cat[0])
    }
    return true
  })
  return (
    <CommonSidebar>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: 64,
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', p: 2 }}>
          <SortIcon sx={{ fontSize: '1rem', color: 'primary.main' }} />
          <Typography
            component="h3"
            variant="overline"
            sx={{ color: 'primary.main' }}
          >
            Report Filters
          </Typography>
          <Typography
            component="h3"
            variant="overline"
            sx={{ color: 'primary.light' }}
          >
            ({filterCount})
          </Typography>
        </Box>
        {filterCount > 0 && (
          <Button onClick={() => dispatch({ type: 'clearFilters' })}>
            Clear
          </Button>
        )}
      </Box>
      <Divider />
      <List sx={{ paddingTop: 0 }}>
        {tenantCategories.map(([catKey, catName]) => (
          <React.Fragment key={catKey}>
            <ListItemButton
              disabled={isEmpty(tags[catKey])}
              onClick={() =>
                setFilterListOpen((state) => ({
                  ...state,
                  [catKey]: !state[catKey],
                }))
              }
            >
              <ListItemText primary={catName} />
              <ChevronRightIcon
                sx={[
                  {
                    transform: 'rotate(0deg)',
                    transition: (theme) =>
                      theme.transitions.create('all', {
                        duration: theme.transitions.duration.shortest,
                      }),
                  },
                  !isEmpty(tags[catKey]) &&
                    filterListOpen[catKey] && {
                      transform: 'rotate(90deg)',
                      color: 'secondary.main',
                    },
                ]}
              />
            </ListItemButton>
            <Collapse in={filterListOpen[catKey]}>
              <List component="div" disablePadding>
                {Object.entries(tags[catKey])
                  .sort((a, b) =>
                    a[0].toLowerCase().localeCompare(b[0].toLowerCase())
                  )
                  .map(([tag, n]) => (
                    <ListItemButton
                      key={tag}
                      sx={[{ pl: 4 }]}
                      onClick={() => {
                        dispatch({
                          type: filters[catKey].includes(tag)
                            ? 'removeFilter'
                            : 'addFilter',
                          category: catKey,
                          tag: tag,
                        })
                      }}
                    >
                      <ListItemIcon>
                        <Checkbox checked={filters[catKey].includes(tag)} />
                      </ListItemIcon>
                      <ListItemText primary={`${tag} (${n})`} />
                    </ListItemButton>
                  ))}
              </List>
            </Collapse>
            <Divider />
          </React.Fragment>
        ))}
      </List>
    </CommonSidebar>
  )
}
